import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useCms } from '../context/CmsContext';

export default function About() {
  const { siteSettings, getPage } = useCms();
  const pageData = getPage('om-os');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Hvem er vi? | Mød ECOLASER</title>
        <meta name="description" content="Hvem er ECOLASER? Læs om virksomheden, der drives af Poul René Nielsen. Med praktisk erfaring og teoretisk grundighed tilbydes kemifri og skånsom laserrens til både fredet kulturarv og erhvervsprojekter." />
        <meta property="og:title" content="Hvem er vi? | Mød ECOLASER" />
        <meta property="og:description" content="Hvem er ECOLASER? Læs om virksomheden, der drives af Poul René Nielsen. Med praktisk erfaring og teoretisk grundighed tilbydes kemifri og skånsom laserrens til både fredet kulturarv og erhvervsprojekter." />
        <meta property="og:image" content="https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/ecolaser-hvid-original-2026-rez.png" />
        <html lang="da" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative pt-44 pb-28 md:pt-52 md:pb-36 overflow-hidden bg-zinc-950">
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/front-kbh-chr-d-9-high.webp?width=1800&quality=80&format=auto"
            alt="Ecolaser baggrund"
            className="w-full h-full object-cover opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-zinc-950 via-zinc-950/60 to-zinc-950/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-left flex flex-col items-start gap-4">
          <h1 className="text-4xl md:text-6xl font-extrabold text-white tracking-tight">
            {(typeof pageData?.title === 'object' ? pageData.title?.da : pageData?.title) || 'Om ECOLASER'}
          </h1>
        </div>
      </section>

      {/* Main Content Section */}
      <section className="bg-white py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
            {/* Left Column: Verbatim Text */}
            <div className="lg:col-span-7 space-y-8">
              {pageData?.content_html?.da ? (
                <div
                  className="prose prose-zinc max-w-none space-y-6 text-zinc-600 leading-relaxed text-sm md:text-base [&>p:first-child]:text-lg [&>p:first-child]:text-zinc-900 [&>p:first-child]:font-semibold [&>p:first-child]:leading-relaxed [&>h2]:text-2xl [&>h2]:font-bold [&>h2]:text-zinc-900 [&>h2]:pt-4 [&>h2]:border-t [&>h2]:border-zinc-200"
                  dangerouslySetInnerHTML={{ __html: pageData.content_html.da }}
                />
              ) : (
                <div className="prose max-w-none space-y-6 text-zinc-600 leading-relaxed text-sm md:text-base">
                  <p className="text-lg text-zinc-900 font-semibold leading-relaxed">
                    Hos ECOLASER er vi specialister i laserrens, som er fremtidens skånsomme, kemifrie og vandfrie metode. Vi rykker ud til alt fra historiske bygninger til præcisionsværktøj i industrien – helt uden at bruge af vores kostbare grundvand. Se flere af vores før- og efterbilleder af laserrens <Link to="/foer-efter" className="text-brand-green hover:underline">her</Link>.
                  </p>
                  <p>
                    ECOLASER er stiftet og drives af Poul René Nielsen. Virksomheden forener solid praktisk erfaring med stærke analytiske og kommunikative kompetencer fra sin uddannelsesbaggrund. Dette sikrer en metodisk tilgang til faget, hvor komplekse projekter gribes struktureret an, og hvor den professionelle dialog med kunden altid er i højsædet.
                  </p>
                  <p>
                    For mig er laserrensning et kompromisløst håndværk, hvor kvalitet, miljø og materialets levetid altid kommer i første række. Hos ECOLASER arbejder jeg udelukkende med professionelt, CE-godkendt udstyr, og alle opgaver udføres i fuld overensstemmelse med Arbejdstilsynets regler for sikkerhed under arbejdet.
                  </p>

                  <h2 className="text-2xl font-bold text-zinc-900 pt-4 border-t border-zinc-200">
                    Fra traditionel facaderens til kemifri laserteknologi
                  </h2>
                  <p>
                    ECOLASER blev etableret på baggrund af mange års erfaring med traditionel facaderens. Med et stærkt ønske om at reducere brugen af kemikalier og store mængder vand, som slider unødigt på materialerne og belaster miljøet, blev fokus flyttet mod et kemifrit alternativ. En af de absolut største fordele ved vores laserteknologi er, at vi overhovedet ikke bruger af vores kostbare grundvand, hvilket gør metoden ekstremt ressourcebesparende og skånsom.
                  </p>
                  <p>
                    I dag tilbyder virksomheden en højteknologisk og skånsom <strong>laserrens</strong>, der genopretter overfladers oprindelige udtryk helt uden mekanisk slid eller kemisk påvirkning. Avanceret laserteknologi gør det muligt at fjerne alt fra rust, kalkslør, salpeterudtræk, biologisk vækst som f.eks. alger, sodskorpe og generel urban tilsmudsning helt uden brug af kemikalier eller vand. <strong>Da metoden er fuldstændig vandfri, er laserrens desuden helt unik til indendørs afrensning (f.eks. i kældre, produktionshaller eller historiske rum), da man slipper for vandskader, fugtophobning og efterfølgende tørretid.</strong>
                  </p>

                  <h2 className="text-2xl font-bold text-zinc-900 pt-4 border-t border-zinc-200">
                    En systematisk og specialistbaseret tilgang
                  </h2>
                  <p>
                    Før enhver renseproces påbegyndes, foretager jeg en dybdegående kortlægning af materialernes tilstand og kemiske sammensætning. Denne systematiske tilgang sikrer korrekt håndtering af komplekse problemstillinger og gør ECOLASER til en specialiseret rådgiver og samarbejdspartner inden for både følsom, <a href="https://slks.dk/" target="_blank" rel="noopener noreferrer" className="text-brand-green hover:underline">fredet kulturarv</a> og større erhvervsprojekter.
                  </p>
                  <p>
                    Processen er ekstremt skånsom, hvilket gør den ideel til alt fra sarte sandstensfigurer og fredede, historiske bygninger til eksklusive, lyse mursten på nybyggeri. Laseren fordamper skidt og forurening på et splitsekund, mens det underliggende materiale efterlades 100 procent intakt.
                  </p>

                  <h2 className="text-2xl font-bold text-zinc-900 pt-4 border-t border-zinc-200">
                    Din samarbejdspartner i hele Danmark
                  </h2>
                  <p>
                    Når du samarbejder med ECOLASER, repræsenterer jeg en synergi mellem praktisk erfaring, teoretisk grundighed og avanceret, kemifri laserteknologi. Du får en erfaren samarbejdspartner, der vurderer opgavens omfang grundigt, så vi finder den mest effektive og skånsomme løsning til netop dit materiale og dine behov.
                  </p>
                  <p>
                    Uanset om du er privat husejer, murermester, industrivirksomhed eller repræsenterer en offentlig institution, står jeg klar til at hjælpe over hele Danmark.
                  </p>
                  <p>
                    Har du en specifik opgave, eller er du i tvivl om, hvad laseren kan gøre for dig? Du er altid velkommen til at kontakte mig for en uforpligtende snak og et professionelt tilbud på dit projekt.
                  </p>
                </div>
              )}
            </div>

            {/* Right Column: Visuals & SMVdanmark */}
            <div className="lg:col-span-5 space-y-8 lg:sticky lg:top-28">
              {/* Poul René Nielsen Image */}
              <div className="bg-zinc-50 border border-zinc-200 rounded-2xl overflow-hidden shadow-xl">
                <div className="aspect-[3/4] w-full relative">
                  <img
                    src={(Array.isArray(pageData?.images) && pageData.images.length > 0) ? `${pageData.images[0]}?width=600&quality=85&format=auto` : "https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/hellerup-3.jpg?width=600&quality=85&format=auto"}
                    alt="Poul René Nielsen - Ejer af ECOLASER"
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              {/* SMVdanmark Medlemskab */}
              <div className="bg-zinc-50 border border-zinc-200 rounded-2xl p-6 md:p-8 flex flex-col gap-4 shadow-xl">
                <img
                  src={(Array.isArray(pageData?.images) && pageData.images.length > 1) ? `${pageData.images[1]}?width=400&quality=85&format=auto` : "https://assets.auxodev.dk/assets/9272dfda-3124-4579-9ee4-d6842e5967c6/smv-logo-rgb-1024x281.jpg"}
                  alt="SMVdanmark Logo"
                  className="h-12 w-auto object-contain self-start"
                />
                <p className="text-zinc-600 text-xs md:text-sm leading-relaxed">
                  For os handler det ikke bare om at levere en effektiv laserrensning – det handler også om at drive en sund og ansvarlig virksomhed. Gennem vores medlemskab af SMVdanmark bakker vi op om det stærke, lokale erhvervsliv og sikrer os de bedste rammer til altid at levere topkvalitet for vores kunder.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="bg-zinc-50 border-y border-zinc-200 py-24">
        <div className="max-w-4xl mx-auto px-6 text-center flex flex-col items-center gap-6">
          <h2 className="text-3xl md:text-5xl font-bold text-zinc-900 leading-tight">
            Lad os tage en snak om dit projekt
          </h2>
          <p className="text-zinc-600 text-sm md:text-base max-w-xl leading-relaxed">
            Vi står klar til at hjælpe dig med professionel rådgivning og et uforpligtende tilbud på din specifikke opgave.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mt-4 w-full sm:w-auto">
            <a
              href={siteSettings.phoneTel}
              className="w-full sm:w-auto bg-brand-green hover:bg-brand-green-light text-white font-bold px-8 py-4 rounded-lg text-sm uppercase tracking-wider transition-all duration-300 flex items-center justify-center gap-2"
            >
              <i className="fa-solid fa-phone" /> Ring direkte: {siteSettings.phone}
            </a>
            <Link
              to="/kontakt"
              className="w-full sm:w-auto bg-white hover:bg-zinc-100 text-zinc-800 font-bold px-8 py-4 rounded-lg text-sm uppercase tracking-wider border border-zinc-200 transition-all duration-300 flex items-center justify-center gap-2"
            >
              <i className="fa-solid fa-envelope" /> Udfyld formular
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
